import { useInput } from '@mui/base';
import cn from 'classnames';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { useHover } from '../../../utils/hooks';
import useForkRefs from '../../../utils/hooks/use-fork-refs';
import { Button } from '../../buttons';
import { CloseIcon } from '../../foundations/icon/icons';

import type { ChangeEvent, ForwardedRef, ReactElement, SyntheticEvent } from 'react';

import type { BaseInputProps } from './base-input.types';

import './base-input.scss';

const defaultRenderInput: BaseInputProps['renderInput'] = (props, ref) => <input {...props} ref={ref} />;

/**
 * @internal
 * The base input component to enter and edit text.
 * Should be used only by other components and not exposed to the end users.
 */
const defaultInputProps: React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> = {};

const BaseInput = forwardRef((props: BaseInputProps, forwardedRef: ForwardedRef<HTMLDivElement>) => {
  const {
    className,
    clearable,
    defaultValue,
    disabled,
    error,
    id,
    inputProps: inInputProps = defaultInputProps,
    inputRef: inInputRef,
    leftElement,
    maxRows,
    minRows,
    name,
    onBlur,
    onChange,
    onClick,
    onCountChange,
    onFocus,
    placeholder,
    readOnly,
    renderInput = defaultRenderInput,
    required,
    rightElement,
    value,
    ...rest
  } = props;
  const uncontrolled = value === undefined || value === null;

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const forkRef = useForkRefs(inputRef, inInputRef);

  const [internalValue, setInternalValue] = useState(uncontrolled ? defaultValue : '');
  const inputValue = uncontrolled ? internalValue : value;

  useEffect(() => {
    onCountChange?.(inputValue?.length ?? 0);
  }, [inputValue, onCountChange]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setInternalValue(e.target.value);
    onChange?.(e);
  };

  const { handlers: hoverHandlers, hovered } = useHover();

  const { focused, getInputProps, getRootProps } = useInput({
    defaultValue,
    disabled,
    error,
    inputRef: forkRef,
    onBlur,
    onChange: handleOnChange,
    onClick,
    onFocus,
    required,
    value: uncontrolled ? undefined : inputValue,
  });

  const handleReset = (e: SyntheticEvent<HTMLButtonElement>): void => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.value = '';
    setInternalValue('');

    const event = Object.create(e, {
      currentTarget: { value: inputRef.current },
      target: { value: inputRef.current },
    });

    onChange?.(event);
  };

  const getClearButton = (): null | ReactElement => {
    const hideClear = disabled || readOnly || !inputValue;

    return (
      <Button
        aria-label="Clear"
        className={cn('rbui-base-input__clear-button', {
          'rbui-base-input__clear-button--hidden': hideClear,
        })}
        onClick={handleReset}
        // Do not trigger onBlur when clear input
        onMouseDown={(e) => e.preventDefault()}
        startIcon={<CloseIcon />}
        type="tertiary"
      />
    );
  };

  const inputContainerClasses = cn('rbui-base-input', className, {
    'rbui-base-input--disabled': disabled,
    'rbui-base-input--error': error,
    'rbui-base-input--focused': focused,
    'rbui-base-input--hovered': hovered,
    'rbui-base-input--readonly': readOnly,
    'rbui-base-input--required': required,
  });

  const inputProps = getInputProps({
    id,
    name,
    placeholder,
    readOnly,
    ...inInputProps,
    className: cn('rbui-base-input__input', inInputProps.className, {
      'rbui-base-input__input--has-left': leftElement,
      'rbui-base-input__input--has-right': clearable || rightElement,
    }),
  });

  return (
    <div className={inputContainerClasses} {...getRootProps({ ...rest, ...hoverHandlers })} ref={forwardedRef}>
      {leftElement && <span className="rbui-base-input__element">{leftElement}</span>}
      {renderInput(inputProps, forkRef)}
      {(rightElement || clearable) && (
        <span className="rbui-base-input__element">
          {clearable && getClearButton()}
          {rightElement}
        </span>
      )}
    </div>
  );
});

BaseInput.displayName = 'BaseInput';

export default BaseInput;
